// Portfolio Administration v1.0 PR 5 — "Add local enforcer" modal for
// the registry list page. Captures the minimum field set from spec §9:
// legal entity name, DBA, type, primary contact, mailing address. The
// rich qualification + insurance fields are recorded on the detail page
// after the row is created in Status='prospect'.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label, Textarea } from "@/design-system/components/ui";
import { useCreateLocalEnforcer } from "@/hooks/portfolioAdmin/useRegistries";
import { ENFORCER_TYPE_OPTIONS } from "./LocalEnforcersListFilters";

export interface CreateLocalEnforcerModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateLocalEnforcerModal({
  open,
  onClose,
}: CreateLocalEnforcerModalProps) {
  const navigate = useNavigate();
  const create = useCreateLocalEnforcer();
  const [legalEntityName, setLegalEntityName] = useState("");
  const [dbaName, setDbaName] = useState("");
  const [enforcerType, setEnforcerType] = useState<string>("law-firm");
  const [primaryContactName, setPrimaryContactName] = useState("");
  const [primaryContactEmail, setPrimaryContactEmail] = useState("");
  const [jurisdictions, setJurisdictions] = useState("");
  const [retainerRequired, setRetainerRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!legalEntityName.trim()) {
      setError("Legal entity name is required.");
      return;
    }
    try {
      const result = await create.mutateAsync({
        legalEntityName: legalEntityName.trim(),
        dbaName: dbaName.trim() || null,
        enforcerType,
        primaryContactName: primaryContactName || null,
        primaryContactEmail: primaryContactEmail || null,
        jurisdictionsServedJson: jurisdictions.trim()
          ? JSON.stringify(
              jurisdictions
                .split(",")
                .map((s) => s.trim().toUpperCase())
                .filter(Boolean),
            )
          : null,
        retainerRequired,
      });
      onClose();
      navigate(`/portfolio-admin/local-enforcers/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create record.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-onyx/40 p-space-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add local enforcer"
      data-testid="create-enforcer-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white-pure rounded-lg shadow-lg p-space-4 flex flex-col gap-space-3 w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <h2 className="font-display font-bold text-h2 m-0">
          Add local enforcer
        </h2>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="enforcer-legal-name">Legal entity name</Label>
          <Input
            id="enforcer-legal-name"
            value={legalEntityName}
            onChange={(e) => setLegalEntityName(e.target.value)}
            required
            data-testid="create-enforcer-legal-name"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="enforcer-dba">DBA name (optional)</Label>
          <Input
            id="enforcer-dba"
            value={dbaName}
            onChange={(e) => setDbaName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="enforcer-type">Type</Label>
          <select
            id="enforcer-type"
            className="px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small"
            value={enforcerType}
            onChange={(e) => setEnforcerType(e.target.value)}
            data-testid="create-enforcer-type"
          >
            {ENFORCER_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="enforcer-contact-name">Primary contact</Label>
          <Input
            id="enforcer-contact-name"
            value={primaryContactName}
            onChange={(e) => setPrimaryContactName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="enforcer-contact-email">
            Primary contact email
          </Label>
          <Input
            id="enforcer-contact-email"
            type="email"
            value={primaryContactEmail}
            onChange={(e) => setPrimaryContactEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <Label htmlFor="enforcer-jurisdictions">
            Jurisdictions (comma-separated state codes)
          </Label>
          <Textarea
            id="enforcer-jurisdictions"
            value={jurisdictions}
            onChange={(e) => setJurisdictions(e.target.value)}
            rows={2}
            placeholder="TX, OK, LA"
          />
        </div>
        <label className="flex items-center gap-space-2 text-small">
          <input
            type="checkbox"
            checked={retainerRequired}
            onChange={(e) => setRetainerRequired(e.target.checked)}
            data-testid="create-enforcer-retainer"
          />
          Retainer required
        </label>
        {error ? (
          <p
            className="text-small text-status-error m-0"
            data-testid="create-enforcer-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={create.isPending}
            data-testid="create-enforcer-submit"
          >
            {create.isPending ? "Creating…" : "Create local enforcer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
